import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/services/api'
import type { EventUserRole } from '@/types'

export const useEventUserRolesStore = defineStore('eventUserRoles', () => {
  // State
  const eventRoles = ref<EventUserRole[]>([])
  const loading = ref(false)
  const loaded = ref(false)
  // Dedupe concurrent loads. On a direct page load App.vue's onMounted and the
  // admin route guard both kick off a load; without sharing the in-flight request
  // the second caller would observe loading===true, do nothing, and read an empty
  // eventRoles — which bounced event-level (non-global) admins off admin routes.
  let inflight: Promise<void> | null = null

  // Getters
  const getRolesByEventId = computed(() => {
    return (eventId: string) => eventRoles.value.filter((r) => r.eventId === eventId)
  })

  const hasRoleForEvent = computed(() => {
    return (eventId: string, role: string) =>
      eventRoles.value.some((r) => r.eventId === eventId && r.role === role)
  })

  // Actions
  async function loadEventUserRoles() {
    // If a load is already running, return the same promise so every caller
    // awaits the same request and sees the populated roles when it resolves.
    if (inflight) return inflight

    inflight = (async () => {
      loading.value = true
      try {
        eventRoles.value = await api.get<EventUserRole[]>('EventUserRole')
        loaded.value = true
      } finally {
        loading.value = false
        inflight = null
      }
    })()

    return inflight
  }

  function reset() {
    eventRoles.value = []
    loaded.value = false
    inflight = null
  }

  return {
    // State
    eventRoles,
    loading,
    loaded,
    // Getters
    getRolesByEventId,
    hasRoleForEvent,
    // Actions
    loadEventUserRoles,
    reset,
  }
})
